import {IonRow, IonSkeletonText, useIonViewWillEnter} from "@ionic/react";
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Profile } from "../../../models/profile";
import { StyledInconsitencyModuleListContent } from "./inconcistencyModule.style";
import {getInconsistencyRecords} from "../../../services/audit.service";
import {getPermission} from "../../../services/permission.service";
import {log} from "../../../services/firebaseAnalytics.service";
import {InconsistencyRecord} from "../../../models/auditReport";
import Pane from "../../../components/pane/pane.component";
import {StyledNoAuditReports} from "../../auditReport/panes/auditReports.style";
import NoOrdersImage from "../../../assets/images/no-orders.svg";
import ReportsImage from "../../../assets/images/chart-pie-36.svg";
import {useHistory} from "react-router-dom";
import {Links} from "../../links";
import {AuditIncompatibilitySource} from "@enums/audit";
import {StyledSocialButton} from "@components/button/button.style";
import AddImage from "@assets/images/e-add.svg";
import {Preferences} from "@capacitor/preferences";
import {ProfileType} from "@enums/profileType";

const MomentRange = require('moment-range');
const Moment = require('moment-timezone')
const moment = MomentRange.extendMoment(Moment);
const momentDurationFormatSetup = require("moment-duration-format");
momentDurationFormatSetup(moment);

export type InconsistencyListItem = {
    date: string;
    name: string;
    place: string;
}

type InconsistencyModuleListPaneProps = {
    topEdge: number | undefined;
    profile?: Profile;
}

const InconsistencyModuleListPane: React.FC<InconsistencyModuleListPaneProps> = (props: InconsistencyModuleListPaneProps) => {
    const { t } = useTranslation();
    const history = useHistory();

    const [loading, setLoading] = useState<boolean>(false);
    const [forceReload] = useState<boolean>(false);
    const [editPermission, setEditPermission] = useState<boolean>(false);
    const [selectedProfile, updateSelectedProfile] = useState<Profile | undefined>();
    const [records, setRecords] = useState<[]|undefined>(undefined);

    const fetchRecords = async () => {
        if ((records === undefined && !loading) || forceReload) {
            setLoading(true);
            setRecords([]);
            await getInconsistencyRecords()
                .then(response => {
                    setRecords(response.data);
                })
                .catch(reason => {
                    setRecords([]);
                });

            setLoading(false);
        }
    }

    const fetchPermission = async () => {
        await getPermission('QUALITY_DEPARTMENT', 'EDIT')
            .then(response => {
                setEditPermission(response.data);
            })
    }

    const openRecord = (record: InconsistencyRecord) => {
        if (editPermission) {
            history.push(Links.inconsistencyRecordEdit + '/' + record.id)
        } else {
            history.push(Links.inconsistencyRecordPreview + '/' + record.id)
        }
    }

    const addIncompatibility= () => {
        history.push(Links.inconsistencyRecordNew)
    }

    const getProfileData = async () => {
        const profileId = await Preferences.get({'key': 'profile_id'});
        const profileType = await Preferences.get({'key': 'profile_type'});

        if (profileId.value !== null && profileType.value !== null) {
            updateSelectedProfile({
                id: parseInt(profileId.value),
                type: profileType.value,
            })
        }
    };

    useEffect(() => {
        fetchPermission();
        getProfileData();
        fetchRecords();
    }, []);

    useIonViewWillEnter( () => {
        fetchPermission();
        getProfileData();
        fetchRecords();
    });

    useEffect(() => {
        log('page_visit', {
            page: 'Audyty -> moduł niezgodności'
        });
    }, []);

    const getPlaceOfServiceName = (record: InconsistencyRecord) => {
        if (record.place_of_service) {
            return record.place_of_service.name
        }

        if (record.place_of_service_manual) {
            return record.place_of_service_manual
        }

        return '';
    }

    return (
        <Pane topEdge={props.topEdge} marginTop={40} paddingBottom={147}>
            {
                editPermission && selectedProfile?.type
                && (selectedProfile.type === ProfileType.QUALITY || selectedProfile.type === ProfileType.COORDINATOR) &&
                <StyledSocialButton className="inverted" onClick={() => addIncompatibility()}>
                    <img src={AddImage}/>
                    {t('inconsistencyTab.addNewButton')}
                </StyledSocialButton>
            }

            {
                !loading && records && records.length === 0 &&
                <StyledNoAuditReports>
                    <img src={NoOrdersImage}/>
                    <img className="reports" src={ReportsImage}/>
                    <span className="message">{t('inconsistencyTab.noRecords')}</span>
                </StyledNoAuditReports>
            }

            {!loading && records && records.length !== 0 &&
                <StyledInconsitencyModuleListContent>
                    <IonRow className="details-header">
                        <div className="details-header__month">{t('inconsistencyTab.subTitle')}</div>
                        <hr className="details-header__hr"/>
                    </IonRow>

                    {records.map((record: InconsistencyRecord, index) => {
                    return (
                        <IonRow className="inconcistency-detail" key={index} onClick={ () => openRecord(record) }>
                            {
                                record.source === AuditIncompatibilitySource.NEGATIVE_AUDIT &&
                                <>
                                    <div className="inconcistency-detail__fs12">{moment(record.audit_report_question.audit_report.date).format('DD.MM.YYYY')}</div>
                                    <div className="inconcistency-detail__fs14">{record.audit_report_question.question}</div>
                                    <div className="inconcistency-detail__fs12">{record.audit_report_question.audit_report.place_of_service?.name}</div>
                                </>
                            }
                            {
                                record.source === AuditIncompatibilitySource.MANUAL &&
                                <>
                                    <div className="inconcistency-detail__fs12">{moment(record.created_at).format('DD.MM.YYYY')}</div>
                                    <div className="inconcistency-detail__fs14">{record.detection_reason}</div>
                                    <div className="inconcistency-detail__fs12">{getPlaceOfServiceName(record)}</div>
                                </>
                            }
                        </IonRow>
                    )
                })}
            </StyledInconsitencyModuleListContent>}

            {
                loading &&
                <StyledInconsitencyModuleListContent>
                    <IonRow className="inconcistency-detail">
                        <div className="inconcistency-detail__fs12"><IonSkeletonText animated style={{ width: '50%' }} /></div>
                        <div className="inconcistency-detail__fs14"><IonSkeletonText animated style={{ width: '30%' }} /></div>
                        <div className="inconcistency-detail__fs12"><IonSkeletonText animated style={{ width: '70%' }} /></div>
                    </IonRow>

                    <IonRow className="inconcistency-detail">
                        <div className="inconcistency-detail__fs12"><IonSkeletonText animated style={{ width: '50%' }} /></div>
                        <div className="inconcistency-detail__fs14"><IonSkeletonText animated style={{ width: '30%' }} /></div>
                        <div className="inconcistency-detail__fs12"><IonSkeletonText animated style={{ width: '70%' }} /></div>
                    </IonRow>

                    <IonRow className="inconcistency-detail">
                        <div className="inconcistency-detail__fs12"><IonSkeletonText animated style={{ width: '50%' }} /></div>
                        <div className="inconcistency-detail__fs14"><IonSkeletonText animated style={{ width: '30%' }} /></div>
                        <div className="inconcistency-detail__fs12"><IonSkeletonText animated style={{ width: '70%' }} /></div>
                    </IonRow>
                </StyledInconsitencyModuleListContent>
            }
        </Pane>
    );
};

export default InconsistencyModuleListPane;
